// The signage forge — procedural PANEL IMAGERY for the night districts.
//
// Why this file exists. The night district passed every measurable law in
// the night-city audit (night city ANALYSIS §e) while rendering flat
// two-tone rectangles with scrawled glyphs on them. The audit's own
// measurements say what the reference actually puts on those rectangles:
//
//   "ad content is cheap real-photo compositing with glyph overlays — a
//    Coca-Cola parody, an unmodified lakeside photograph as a travel ad,
//    movie-poster strips. Text is ornament, not copy."
//
// A photograph is not a gradient. What separates the two, at 1:1, is a
// short list of things a canvas can do and a two-stop gradient cannot:
//
//   1. PICTORIAL DEPTH — a foreground, a middle and a far, each lighter and
//      bluer than the one in front of it (aerial perspective is what makes
//      a painted ridge read as a photographed one).
//   2. A DIRECTIONAL KEY — every form brighter on one side, darker on the
//      other, plus a rim on the far edge. Flat fills have no light in them.
//   3. SOFT EDGES — forms built from radial gradients and blurs, never from
//      hard vector boundaries.
//   4. GRAIN AND A GRADE — a per-pixel noise floor and a duotone lift.
//      Nothing says "vector art" louder than a mathematically clean field.
//
// Everything below is generated ONCE at scene build, into canvases that
// become static textures. There is no per-frame work in this file at all;
// the cost is milliseconds of load and zero frame time. No art files.
//
// Each archetype also yields two DERIVED sheets, because a sign that lights
// nothing is a sticker:
//   - `spill`  — a heavy blur of its own face, used as an additive decal on
//                the wall behind and around it. The light a sign throws has
//                the sign's colours and roughly the sign's shape.
//   - `reflect`— the face smeared downward and broken by ripple bands: the
//                wet-ground reflection.

import * as THREE from "three";
import type { Tracker } from "./common";
import { hash1, makeFbm, mulberry } from "./forge";

// ---------------------------------------------------------------------------
// Canvas plumbing

interface Sheet {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  w: number;
  h: number;
}

function sheet(w: number, h: number): Sheet {
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  // `willReadFrequently` matters: every archetype ends with a getImageData
  // grade pass, and without the hint Chrome keeps the canvas GPU-side and
  // pays a readback stall per poster.
  const ctx = canvas.getContext("2d", { willReadFrequently: true }) as CanvasRenderingContext2D;
  return { canvas, ctx, w, h };
}

function texture(canvas: HTMLCanvasElement, t: Tracker): THREE.CanvasTexture {
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  tex.needsUpdate = true;
  return t.tex(tex);
}

/** Separable box blur over RGBA, clamped at the edges. Three passes is a
 *  close enough gaussian for glow, at a third of the cost. */
function blurRgba(data: Uint8ClampedArray, w: number, h: number, radius: number): void {
  if (radius < 1) return;
  const tmp = new Uint8ClampedArray(data.length);
  const span = radius * 2 + 1;
  for (let pass = 0; pass < 3; pass++) {
    // Horizontal.
    for (let y = 0; y < h; y++) {
      const row = y * w * 4;
      for (let c = 0; c < 4; c++) {
        let sum = 0;
        for (let k = -radius; k <= radius; k++) {
          sum += data[row + Math.min(w - 1, Math.max(0, k)) * 4 + c] ?? 0;
        }
        for (let x = 0; x < w; x++) {
          tmp[row + x * 4 + c] = sum / span;
          sum -= data[row + Math.min(w - 1, Math.max(0, x - radius)) * 4 + c] ?? 0;
          sum += data[row + Math.min(w - 1, Math.max(0, x + radius + 1)) * 4 + c] ?? 0;
        }
      }
    }
    // Vertical.
    for (let x = 0; x < w; x++) {
      for (let c = 0; c < 4; c++) {
        let sum = 0;
        for (let k = -radius; k <= radius; k++) {
          sum += tmp[Math.min(h - 1, Math.max(0, k)) * w * 4 + x * 4 + c] ?? 0;
        }
        for (let y = 0; y < h; y++) {
          data[y * w * 4 + x * 4 + c] = sum / span;
          sum -= tmp[Math.min(h - 1, Math.max(0, y - radius)) * w * 4 + x * 4 + c] ?? 0;
          sum += tmp[Math.min(h - 1, Math.max(0, y + radius + 1)) * w * 4 + x * 4 + c] ?? 0;
        }
      }
    }
  }
}

/**
 * Blur a rectangle of an OPAQUE sheet in place.
 *
 * This is the single most important call in the file and it deserves the
 * explanation. What separates a photograph from vector art, at 1:1, is that
 * a photograph has no mathematically exact edges: a lens, a film stock and a
 * subject's own soft shading conspire so that every boundary takes two or
 * three pixels to happen. Canvas's `arc`/`fill` produce a one-pixel
 * antialiased edge on a perfect ellipse, which the eye reads instantly as
 * "drawn". So every archetype builds its FORMS, softens them here, and only
 * then lays the sharp things a photograph does have — a specular kick, a rim
 * light, the printed type — back on top.
 *
 * Opaque only, deliberately: blurring straight RGBA drags colour toward the
 * transparent black of unpainted texels and fringes every edge dark.
 */
function soften(
  ctx: CanvasRenderingContext2D,
  radius: number,
  x = 0,
  y = 0,
  w = ctx.canvas.width,
  h = ctx.canvas.height,
): void {
  const ox = Math.max(0, Math.round(x));
  const oy = Math.max(0, Math.round(y));
  const rw = Math.min(ctx.canvas.width - ox, Math.round(w));
  const rh = Math.min(ctx.canvas.height - oy, Math.round(h));
  if (rw < 3 || rh < 3 || radius < 1) return;
  const img = ctx.getImageData(ox, oy, rw, rh);
  blurRgba(img.data, rw, rh, radius);
  ctx.putImageData(img, ox, oy);
}

export interface GradeOptions {
  /** Per-pixel luma noise, 0–1 of full scale. The single cheapest thing
   *  that stops a canvas reading as vector art. */
  grain?: number;
  /** Duotone: shadow and highlight tints the image is remapped between,
   *  by weight. A printed poster in a fogged street is never neutral. */
  shadow?: [number, number, number];
  highlight?: [number, number, number];
  tone?: number;
  /** Corner falloff, 0–1 — a lens/print vignette. */
  vignette?: number;
  /** Multiply after grading; > 1 for the fixtures that must clear Y200. */
  gain?: number;
  /** Ceiling. The night bar's one hard clip law is "no clipped whites
   *  in-scene: brightest sign cores ≤ ~225". Enforced here, at the source,
   *  rather than hoped for downstream. */
  ceiling?: number;
  seed?: number;
}

/**
 * The finishing pass every archetype ends with: grain, duotone grade,
 * vignette, gain and the 225 ceiling, in one walk of the buffer.
 */
function grade(s: Sheet, opts: GradeOptions = {}): void {
  const { w, h, ctx } = s;
  const img = ctx.getImageData(0, 0, w, h);
  const d = img.data;
  const grain = opts.grain ?? 0.055;
  const tone = opts.tone ?? 0;
  const sh = opts.shadow ?? [0.1, 0.16, 0.2];
  const hi = opts.highlight ?? [1.0, 0.99, 0.96];
  const vig = opts.vignette ?? 0.22;
  const gain = opts.gain ?? 1;
  const ceiling = opts.ceiling ?? 225;
  const rnd = mulberry((opts.seed ?? 7) * 2654435761);
  const cx = w / 2;
  const cy = h / 2;
  const maxR = Math.hypot(cx, cy);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      let r = d[i] ?? 0;
      let g = d[i + 1] ?? 0;
      let b = d[i + 2] ?? 0;
      if (tone > 0) {
        // Map luminance onto the shadow→highlight ramp and cross-fade. This
        // is what makes five different posters look like they were printed
        // by the same press and hung in the same fog.
        const l = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
        const tr = (sh[0] + (hi[0] - sh[0]) * l) * 255;
        const tg = (sh[1] + (hi[1] - sh[1]) * l) * 255;
        const tb = (sh[2] + (hi[2] - sh[2]) * l) * 255;
        r += (tr - r) * tone;
        g += (tg - g) * tone;
        b += (tb - b) * tone;
      }
      if (vig > 0) {
        const k = 1 - vig * (Math.hypot(x - cx, y - cy) / maxR) ** 2.2;
        r *= k;
        g *= k;
        b *= k;
      }
      if (grain > 0) {
        // Correlated across channels (film grain is density, not colour) with
        // a little chroma break, and stronger in the mids than the blacks.
        const n = (rnd() - 0.5) * 255 * grain;
        const c = (rnd() - 0.5) * 255 * grain * 0.4;
        r += n + c;
        g += n;
        b += n - c;
      }
      d[i] = Math.min(ceiling, Math.max(0, r * gain));
      d[i + 1] = Math.min(ceiling, Math.max(0, g * gain));
      d[i + 2] = Math.min(ceiling, Math.max(0, b * gain));
    }
  }
  ctx.putImageData(img, 0, 0);
}

// ---------------------------------------------------------------------------
// The brush script
//
// The r3 verdict on the old glyphs was that they read as "hand-scrawled fake
// glyphs … the exact tell of a scene where nobody has yet decided what
// anything says", and it was right, because they were drawn the way nobody
// writes: uniform-width polyline strokes at small size, twenty to a row.
// The reference's banner carries FIVE glyph forms down a ten-metre banner,
// each a thick calligraphic stroke with a loaded head and a whipped tail.
//
// So: strokes are stamped, not stroked. A stroke is a spline sampled at
// ~2 px and stamped with a disc whose radius follows a pressure curve —
// which is literally how a brush lays ink, and gives tapers, belly and
// blunt heads for free.

type Pt = readonly [number, number];
interface Stroke {
  /** Control points in glyph-local [0,1]², y down. */
  readonly pts: readonly Pt[];
  /** Pressure at the head and the tail, as a fraction of the nib. */
  readonly p0: number;
  readonly p1: number;
}

/** Catmull-Rom through the control points, so two-point strokes stay
 *  straight and four-point strokes curl. */
function splineAt(pts: readonly Pt[], t: number): Pt {
  const n = pts.length;
  if (n === 1) return pts[0] ?? [0, 0];
  if (n === 2) {
    const a = pts[0] ?? [0, 0];
    const b = pts[1] ?? [0, 0];
    return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
  }
  const seg = Math.min(n - 2, Math.floor(t * (n - 1)));
  const lt = t * (n - 1) - seg;
  const p = (i: number): Pt => pts[Math.min(n - 1, Math.max(0, i))] ?? [0, 0];
  const p0 = p(seg - 1);
  const p1 = p(seg);
  const p2 = p(seg + 1);
  const p3 = p(seg + 2);
  const t2 = lt * lt;
  const t3 = t2 * lt;
  const c = (a: number, b: number, cc: number, dd: number): number =>
    0.5 *
    (2 * b + (cc - a) * lt + (2 * a - 5 * b + 4 * cc - dd) * t2 + (-a + 3 * b - 3 * cc + dd) * t3);
  return [c(p0[0], p1[0], p2[0], p3[0]), c(p0[1], p1[1], p2[1], p3[1])];
}

/** The alphabet. Twelve forms sharing one hand: a loaded downstroke, a
 *  crossing or a bowl, and a tail that lifts off the surface. */
const ALPHABET: readonly (readonly Stroke[])[] = [
  // 1 — downstroke with a bottom hook
  [
    {
      pts: [
        [0.28, 0.06],
        [0.34, 0.42],
        [0.3, 0.74],
        [0.66, 0.9],
      ],
      p0: 1,
      p1: 0.18,
    },
  ],
  // 2 — bowl over a spine
  [
    {
      pts: [
        [0.18, 0.2],
        [0.5, 0.06],
        [0.8, 0.24],
        [0.56, 0.42],
      ],
      p0: 0.85,
      p1: 0.3,
    },
    {
      pts: [
        [0.5, 0.3],
        [0.46, 0.92],
      ],
      p0: 0.95,
      p1: 0.25,
    },
  ],
  // 3 — a whipped S
  [
    {
      pts: [
        [0.78, 0.1],
        [0.3, 0.2],
        [0.7, 0.6],
        [0.2, 0.9],
      ],
      p0: 0.9,
      p1: 0.15,
    },
  ],
  // 4 — twin uprights bridged
  [
    {
      pts: [
        [0.24, 0.08],
        [0.22, 0.9],
      ],
      p0: 1,
      p1: 0.4,
    },
    {
      pts: [
        [0.72, 0.1],
        [0.74, 0.86],
      ],
      p0: 0.8,
      p1: 0.3,
    },
    {
      pts: [
        [0.24, 0.5],
        [0.74, 0.4],
      ],
      p0: 0.45,
      p1: 0.45,
    },
  ],
  // 5 — a wide chevron with a dropped tail
  [
    {
      pts: [
        [0.12, 0.16],
        [0.5, 0.6],
        [0.88, 0.14],
      ],
      p0: 0.9,
      p1: 0.22,
    },
    {
      pts: [
        [0.5, 0.58],
        [0.54, 0.94],
      ],
      p0: 0.5,
      p1: 0.2,
    },
  ],
  // 6 — an open loop
  [
    {
      pts: [
        [0.8, 0.24],
        [0.4, 0.1],
        [0.16, 0.5],
        [0.46, 0.88],
        [0.84, 0.72],
      ],
      p0: 0.8,
      p1: 0.2,
    },
  ],
  // 7 — cap and diagonal
  [
    {
      pts: [
        [0.16, 0.14],
        [0.84, 0.1],
      ],
      p0: 0.9,
      p1: 0.35,
    },
    {
      pts: [
        [0.72, 0.16],
        [0.24, 0.9],
      ],
      p0: 0.85,
      p1: 0.16,
    },
  ],
  // 8 — spine with two ticks
  [
    {
      pts: [
        [0.3, 0.06],
        [0.3, 0.92],
      ],
      p0: 1,
      p1: 0.3,
    },
    {
      pts: [
        [0.32, 0.3],
        [0.76, 0.2],
      ],
      p0: 0.55,
      p1: 0.2,
    },
    {
      pts: [
        [0.32, 0.62],
        [0.8, 0.54],
      ],
      p0: 0.5,
      p1: 0.18,
    },
  ],
  // 9 — a scythe
  [
    {
      pts: [
        [0.2, 0.08],
        [0.62, 0.28],
        [0.7, 0.66],
        [0.26, 0.9],
      ],
      p0: 0.95,
      p1: 0.2,
    },
  ],
  // 10 — crossed strokes
  [
    {
      pts: [
        [0.16, 0.12],
        [0.84, 0.86],
      ],
      p0: 0.95,
      p1: 0.18,
    },
    {
      pts: [
        [0.82, 0.16],
        [0.2, 0.82],
      ],
      p0: 0.7,
      p1: 0.18,
    },
  ],
  // 11 — a curl over a base
  [
    {
      pts: [
        [0.24, 0.42],
        [0.5, 0.08],
        [0.78, 0.36],
        [0.5, 0.5],
      ],
      p0: 0.75,
      p1: 0.28,
    },
    {
      pts: [
        [0.18, 0.84],
        [0.84, 0.76],
      ],
      p0: 0.7,
      p1: 0.3,
    },
  ],
  // 12 — a heavy bar and a stem
  [
    {
      pts: [
        [0.14, 0.24],
        [0.86, 0.18],
      ],
      p0: 1,
      p1: 0.5,
    },
    {
      pts: [
        [0.52, 0.22],
        [0.46, 0.94],
      ],
      p0: 0.8,
      p1: 0.22,
    },
  ],
];

/** Stamp one stroke as a loaded brush: discs along the spline, radius on a
 *  pressure curve with a little belly. */
function stampStroke(
  ctx: CanvasRenderingContext2D,
  st: Stroke,
  x: number,
  y: number,
  size: number,
  nib: number,
  jitter: (k: number) => number,
): void {
  const steps = Math.max(10, Math.round(size * 0.5));
  ctx.beginPath();
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const [gx, gy] = splineAt(st.pts, t);
    // Pressure: head weight, a slight belly at a third, then the lift.
    const press = st.p0 + (st.p1 - st.p0) * t + Math.sin(t * Math.PI) * 0.16;
    const r = Math.max(0.35, nib * press);
    const jx = jitter(i) * size * 0.012;
    const jy = jitter(i + 97) * size * 0.012;
    ctx.moveTo(x + gx * size + jx + r, y + gy * size + jy);
    ctx.arc(x + gx * size + jx, y + gy * size + jy, r, 0, Math.PI * 2);
  }
  ctx.fill();
}

export interface GlyphOptions {
  /** Ink colour. */
  color?: string;
  /** A wet edge drawn under the ink — paint bleeds into the substrate. */
  halo?: string;
  /** Nib width as a fraction of glyph size. */
  weight?: number;
  /** Stack the glyphs down instead of across (banner columns). */
  vertical?: boolean;
  /** Rotate each glyph a little; real lettering is never on a rail. */
  wobble?: number;
}

/**
 * A run of glyphs. `count` forms, laid across (or down) from x,y, each
 * `size` on its long axis. Never Latin, never readable — the audit is
 * explicit that the reference's ad text is ornament.
 */
export function glyphRun(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  count: number,
  seed: number,
  opts: GlyphOptions = {},
): void {
  const color = opts.color ?? "#f2f5f6";
  const weight = opts.weight ?? 0.115;
  const wobble = opts.wobble ?? 0.09;
  const nib = size * weight;
  const jitter = (k: number): number => hash1(seed * 7919 + 13, k) - 0.5;
  for (let i = 0; i < count; i++) {
    const form = ALPHABET[Math.floor(hash1(seed, i) * ALPHABET.length) % ALPHABET.length];
    if (!form) continue;
    const step = size * (0.86 + hash1(seed + 3, i) * 0.24);
    const gx = opts.vertical ? x + (hash1(seed + 5, i) - 0.5) * size * 0.14 : x + i * step;
    const gy = opts.vertical ? y + i * step : y + (hash1(seed + 5, i) - 0.5) * size * 0.1;
    ctx.save();
    ctx.translate(gx + size / 2, gy + size / 2);
    ctx.rotate((hash1(seed + 11, i) - 0.5) * wobble);
    ctx.translate(-size / 2, -size / 2);
    if (opts.halo) {
      ctx.fillStyle = opts.halo;
      for (const st of form) stampStroke(ctx, st, 0, 0, size, nib * 1.45, jitter);
    }
    ctx.fillStyle = color;
    for (const st of form) stampStroke(ctx, st, 0, 0, size, nib, jitter);
    ctx.restore();
  }
}

// ---------------------------------------------------------------------------
// Pictorial primitives
//
// The three things a photograph has that a gradient does not, as functions.

/** A soft-edged form: a radial gradient ellipse. Every shape in a poster is
 *  one of these, because a hard vector edge is the whole tell. */
function blob(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  rx: number,
  ry: number,
  inner: string,
  outer: string,
  softness = 0.55,
): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(1, ry / rx);
  const g = ctx.createRadialGradient(0, 0, rx * (1 - softness), 0, 0, rx);
  g.addColorStop(0, inner);
  g.addColorStop(1, outer);
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(0, 0, rx, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/** A lit form: fill a path with a directional ramp, so the shape has a key
 *  side and a shadow side. Light comes from `ang` radians. */
function keyFill(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  r: number,
  lit: string,
  dark: string,
  ang: number,
): CanvasGradient {
  const g = ctx.createLinearGradient(
    x - Math.cos(ang) * r,
    y - Math.sin(ang) * r,
    x + Math.cos(ang) * r,
    y + Math.sin(ang) * r,
  );
  g.addColorStop(0, lit);
  g.addColorStop(0.55, lit);
  g.addColorStop(1, dark);
  return g;
}

const clamp255 = (v: number): number => Math.min(255, Math.max(0, Math.round(v)));

/** Mix two hex colours in sRGB — good enough for aerial perspective, which
 *  is what this is for. */
function mix(a: string, b: string, t: number): string {
  const pa = Number.parseInt(a.slice(1), 16);
  const pb = Number.parseInt(b.slice(1), 16);
  const r = clamp255((pa >> 16) + (((pb >> 16) & 255) - (pa >> 16)) * t);
  const g = clamp255(((pa >> 8) & 255) + (((pb >> 8) & 255) - ((pa >> 8) & 255)) * t);
  const bl = clamp255((pa & 255) + ((pb & 255) - (pa & 255)) * t);
  return `#${((r << 16) | (g << 8) | bl).toString(16).padStart(6, "0")}`;
}

/**
 * A ridge line across the sheet: fbm-driven, filled to the bottom, tinted
 * toward the sky by `depth`. Stack four of these and the poster has air in
 * it — this one function is most of why the travel poster reads as a photo.
 */
function ridge(
  ctx: CanvasRenderingContext2D,
  w: number,
  baseY: number,
  amp: number,
  seed: number,
  near: string,
  sky: string,
  depth: number,
  bottom: number,
): void {
  const fbm = makeFbm(seed, [3, 7, 17]);
  const ridged = makeFbm(seed + 55, [5, 13]);
  const body = mix(near, sky, depth);
  const g = ctx.createLinearGradient(0, baseY - amp, 0, bottom);
  g.addColorStop(0, mix(body, "#ffffff", 0.1 * (1 - depth)));
  g.addColorStop(1, mix(body, sky, 0.35));
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.moveTo(0, bottom);
  for (let x = 0; x <= w; x += 2) {
    // Two terms: an fbm for the massif and a ridged term for the peaks.
    // A pure fbm skyline is a row of soft humps; mountains have points.
    const n = fbm(x / w, 0.5);
    const peak = (1 - Math.abs(ridged(x / w, 0.31) * 2 - 1)) ** 2.2;
    ctx.lineTo(x, baseY - (n - 0.5) * amp * 2 - peak * amp * 1.1);
  }
  ctx.lineTo(w, bottom);
  ctx.closePath();
  ctx.fill();
}

// ---------------------------------------------------------------------------
// The panel: a face plus the light it throws

export interface Panel {
  /** The lit face, for an unlit quad. */
  face: THREE.CanvasTexture;
  /** A heavy blur of the face, additive on the wall behind: the glow that
   *  bleeds onto adjacent geometry. */
  spill: THREE.CanvasTexture;
  /** The face smeared and ripple-broken, additive on the wet ground. */
  reflect: THREE.CanvasTexture;
  /** Dominant accent, css — for the anchored haze sprite. */
  tint: string;
  /** Mean sRGB luma of the face: how hard this fixture should throw. */
  power: number;
  aspect: number;
}

/** Which of the two accent families this face mostly belongs to, plus how
 *  bright it is — the two numbers the mounting code needs. */
function analyse(s: Sheet): { tint: string; power: number } {
  const d = s.ctx.getImageData(0, 0, s.w, s.h).data;
  let r = 0;
  let g = 0;
  let b = 0;
  let y = 0;
  const n = s.w * s.h;
  for (let i = 0; i < n; i++) {
    const pr = d[i * 4] ?? 0;
    const pg = d[i * 4 + 1] ?? 0;
    const pb = d[i * 4 + 2] ?? 0;
    const l = 0.2126 * pr + 0.7152 * pg + 0.0722 * pb;
    y += l;
    // Weight by luma²: the tint of a sign is the tint of its bright parts,
    // not of its bezel.
    const wgt = (l / 255) ** 2;
    r += pr * wgt;
    g += pg * wgt;
    b += pb * wgt;
  }
  // Push the mean toward full saturation before handing it out.
  //
  // Measured, and it cost a whole accent family. The hero banner's own
  // pixel mean is near-white (two accents plus white brush glyphs), so an
  // unsaturated tint gave it a near-white haze patch, that patch is
  // additive, and it sat over the banner and dragged its cyan half from
  // S46% — the reference's measured figure — down to S21%. Family A then
  // read 1.2% of emissive against the reference's 22.5%. A sign's GLOW is
  // the colour of its brightest saturated light, not the average of its
  // whole face, which is also what a real fixture does in fog.
  const m = Math.max(1e-3, Math.max(r, g, b));
  const lo = Math.min(r, g, b) / m;
  const boost = (v: number): number =>
    Math.round(Math.min(1, (v / m - lo * 0.72) / (1 - lo * 0.72)) * 255);
  return {
    tint: `rgb(${boost(r)},${boost(g)},${boost(b)})`,
    power: y / n,
  };
}

/** Derive the spill sheet: downsample (blur is cheap on a small buffer and
 *  the result is blurry by definition), blur hard, lift, and fade to zero at
 *  the border so the decal has no edge. */
function spillFrom(src: Sheet, t: Tracker, boost: number): THREE.CanvasTexture {
  const sw = 64;
  const sh = Math.max(16, Math.round((64 * src.h) / src.w));
  // The margin is where the light gets to travel past the sign's edge.
  const mw = Math.round(sw * 1.55);
  const mh = Math.round(sh * 1.55);
  const s = sheet(mw, mh);
  s.ctx.fillStyle = "#000000";
  s.ctx.fillRect(0, 0, mw, mh);
  s.ctx.drawImage(src.canvas, (mw - sw) / 2, (mh - sh) / 2, sw, sh);
  const img = s.ctx.getImageData(0, 0, mw, mh);
  blurRgba(img.data, mw, mh, Math.max(3, Math.round(Math.min(mw, mh) * 0.16)));
  const d = img.data;
  const cx = mw / 2;
  const cy = mh / 2;
  for (let y = 0; y < mh; y++) {
    for (let x = 0; x < mw; x++) {
      const i = (y * mw + x) * 4;
      // Radial falloff to hard zero at the border: an additive decal with a
      // live edge is a visible rectangle of light on a wall.
      //
      // Cubed, not squared. §e's bloom law is measured: "residual halo ≤ +20
      // over ambient, fully decayed within ~100 px… glow lives in the haze,
      // not the bloom radius". A squared falloff put +40 of glow 250 px out
      // from every fixture, which took the frame's Y≥60 coverage to 14.3%
      // against a ≤10% bar and its YHIGH to 71 against a 50–64 window. The
      // light still bleeds onto the wall; it just stops owning the canyon.
      const k =
        Math.max(0, 1 - (Math.abs(x - cx) / cx) ** 2) *
        Math.max(0, 1 - (Math.abs(y - cy) / cy) ** 2);
      const f = k * k * k * boost;
      d[i] = clamp255((d[i] ?? 0) * f);
      d[i + 1] = clamp255((d[i + 1] ?? 0) * f);
      d[i + 2] = clamp255((d[i + 2] ?? 0) * f);
      d[i + 3] = 255;
    }
  }
  s.ctx.putImageData(img, 0, 0);
  return texture(s.canvas, t);
}

/**
 * Derive the wet-ground reflection: the face flipped, stretched, and each
 * scanline offset by a travelling ripple, with the whole thing fading with
 * distance from the fixture's foot. Drawn once, at load, one scanline at a
 * time — which is what a wet street actually does to an image.
 */
function reflectFrom(src: Sheet, t: Tracker, seed: number, gain: number): THREE.CanvasTexture {
  const w = 96;
  const h = 192;
  const s = sheet(w, h);
  s.ctx.fillStyle = "#000000";
  s.ctx.fillRect(0, 0, w, h);
  const rippleA = makeFbm(seed, [4, 11]);
  const rippleB = makeFbm(seed + 41, [7, 23]);
  for (let y = 0; y < h; y++) {
    const v = y / h;
    // Perspective: the reflection stretches as it comes toward the viewer,
    // so the source row advances fastest near the fixture.
    const srcV = v ** 1.7;
    const sy = Math.min(src.h - 1, Math.max(0, Math.round((1 - srcV) * (src.h - 1))));
    const wob = (rippleA(v * 3, 0.3) - 0.5) * w * 0.16 * (0.3 + v);
    const squash = 1 + v * 0.55;
    s.ctx.globalAlpha = Math.max(0, (1 - v) ** 1.5) * (0.55 + rippleB(v * 6, 0.7) * 0.5);
    s.ctx.drawImage(src.canvas, 0, sy, src.w, 1, (w * (1 - squash)) / 2 + wob, y, w * squash, 1.4);
  }
  s.ctx.globalAlpha = 1;
  // Break it with dark ripple troughs: an unbroken smear reads as a mirror,
  // and a wet street is not a mirror.
  const img = s.ctx.getImageData(0, 0, w, h);
  const d = img.data;
  for (let y = 0; y < h; y++) {
    const v = y / h;
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      const trough =
        0.55 +
        0.45 * Math.sin(v * 26 + rippleA(x / w, v) * 7) * (0.4 + v * 0.6) +
        (rippleB((x / w) * 2, v * 4) - 0.5) * 0.5;
      // Sides fade too — a puddle has a shore.
      const edge = Math.max(0, 1 - (Math.abs(x - w / 2) / (w / 2)) ** 2.4);
      const f = Math.max(0, trough) * edge * gain;
      d[i] = clamp255((d[i] ?? 0) * f);
      d[i + 1] = clamp255((d[i + 1] ?? 0) * f);
      d[i + 2] = clamp255((d[i + 2] ?? 0) * f);
      d[i + 3] = 255;
    }
  }
  s.ctx.putImageData(img, 0, 0);
  return texture(s.canvas, t);
}

export interface PanelOptions extends GradeOptions {
  /** Extra punch on the spill decal (hero fixtures throw further). */
  spillBoost?: number;
  /** Extra punch on the wet-ground reflection. */
  reflectGain?: number;
  /** Skip the reflection sheet for fixtures too high or too far to cast one. */
  noReflect?: boolean;
}

/** Draw a face, then derive its light. One entry point for every archetype. */
export function makePanel(
  t: Tracker,
  w: number,
  h: number,
  draw: (ctx: CanvasRenderingContext2D, w: number, h: number) => void,
  opts: PanelOptions = {},
): Panel {
  const s = sheet(w, h);
  draw(s.ctx, w, h);
  grade(s, opts);
  const { tint, power } = analyse(s);
  return {
    face: texture(s.canvas, t),
    spill: spillFrom(s, t, opts.spillBoost ?? 1.15),
    reflect: opts.noReflect
      ? spillFrom(s, t, 0.6)
      : reflectFrom(s, t, (opts.seed ?? 3) * 131 + 7, opts.reflectGain ?? 1.25),
    tint,
    power,
    aspect: w / h,
  };
}

// ---------------------------------------------------------------------------
// The archetypes
//
// Six, matching the audit's census of the reference's six: a hanging
// double-faced banner, a backlit photo-poster, a product/brand panel, a
// figure-strip stack, a wall screen, and a flush neon. Bebop's own subjects
// — aether liners, the ridge country, the trades — never the reference's IP.

/** The two accent families, as the census sees them. */
export const ACCENT_A = "#39d4e6"; // aether-teal, H187
export const ACCENT_A_DEEP = "#0e6f80";
export const ACCENT_B = "#f24a60"; // signal-crimson, H353, Y112
export const ACCENT_B_DEEP = "#7a1930";
export const INK = "#0b1116";
export const PAPER = "#dfe6e8";

/**
 * THE TRAVEL POSTER — the reference's single most characteristic fixture is
 * "an unmodified Italian-lakeside photograph" behind glass with a glyph
 * header over it, and it is also the easiest photograph to forge, because
 * what makes a landscape photo read as a photo is aerial perspective and
 * nothing else. Four ridge layers each mixed further toward the sky, a
 * shoreline of small lit buildings, water with vertical smears of the town
 * and horizontal ripple lines. Bebop's version is its own ridge country.
 */
export function drawTravelPoster(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  seed = 1,
): void {
  const sky = "#b9dbe8";
  const skyHi = "#e8f3f6";
  const horizon = h * 0.62;
  // Sky: bright at the horizon, deepening up — the photograph's own key.
  const gs = ctx.createLinearGradient(0, 0, 0, horizon);
  gs.addColorStop(0, "#5f9ec0");
  gs.addColorStop(0.55, mix(sky, "#5f9ec0", 0.35));
  gs.addColorStop(1, skyHi);
  ctx.fillStyle = gs;
  ctx.fillRect(0, 0, w, h);
  // A haze bloom over the sun, low left.
  blob(
    ctx,
    w * 0.28,
    horizon * 0.92,
    w * 0.5,
    h * 0.22,
    "rgba(255,252,238,0.75)",
    "rgba(255,252,238,0)",
    0.95,
  );

  // Four ridges, receding. Depth drives the mix toward the sky — this is the
  // aerial-perspective term, and it is the whole illusion.
  ridge(ctx, w, horizon - h * 0.2, h * 0.075, seed + 1, "#2c4d63", sky, 0.72, horizon);
  ridge(ctx, w, horizon - h * 0.14, h * 0.055, seed + 2, "#22475c", sky, 0.52, horizon);
  ridge(ctx, w, horizon - h * 0.085, h * 0.04, seed + 3, "#1b3b4d", sky, 0.3, horizon);
  ridge(ctx, w, horizon - h * 0.035, h * 0.022, seed + 4, "#16303f", sky, 0.12, horizon);

  // The shore town: TWO ranks of small blocks, the back one hazed and
  // raised up the hillside, the front one on the water. One rank on one
  // baseline is a bar chart; two overlapping ranks are a town.
  const townY = horizon - h * 0.012;
  for (const rank of [1, 0] as const) {
    const haze = rank === 1 ? 0.42 : 0.1;
    const lift = rank === 1 ? h * 0.045 : 0;
    for (let i = 0; i < 34; i++) {
      const k = rank * 100 + i;
      const bx = w * (0.02 + hash1(seed + 20, k) * 0.96);
      const bw = w * (0.018 + hash1(seed + 21, k) * 0.042);
      const bh = h * (0.022 + hash1(seed + 22, k) ** 1.6 * 0.085) * (rank === 1 ? 0.8 : 1);
      const v = hash1(seed + 23, k);
      // The shore is not level: the hill runs up to the right.
      const base = townY - lift - (bx / w) * h * 0.02 * rank;
      const wall = mix(v > 0.6 ? "#efe2ca" : v > 0.3 ? "#dcc9ad" : "#c0a98f", sky, 0.16 + haze);
      ctx.fillStyle = wall;
      ctx.fillRect(bx, base - bh, bw, bh);
      // Clay roof, and the flank the sun does not reach.
      ctx.fillStyle = mix("#a05b40", sky, 0.24 + haze);
      ctx.fillRect(bx - bw * 0.06, base - bh, bw * 1.12, Math.max(1, bh * 0.15));
      ctx.fillStyle = `rgba(28,52,70,${0.3 - haze * 0.4})`;
      ctx.fillRect(bx + bw * 0.7, base - bh, bw * 0.3, bh);
      // Window specks: warm, one or two, sub-pixel where this poster hangs.
      if (v > 0.45 && rank === 0) {
        ctx.fillStyle = "rgba(255,224,170,0.9)";
        ctx.fillRect(
          bx + bw * 0.22,
          base - bh * 0.6,
          Math.max(1, bw * 0.15),
          Math.max(1, bh * 0.12),
        );
        if (v > 0.75)
          ctx.fillRect(
            bx + bw * 0.58,
            base - bh * 0.35,
            Math.max(1, bw * 0.13),
            Math.max(1, bh * 0.1),
          );
      }
    }
  }
  // The campanile: every lakeside photograph has one, and it is what makes
  // a row of rectangles read as a town rather than as a bar chart.
  const cx = w * 0.63;
  ctx.fillStyle = mix("#efe3cd", sky, 0.2);
  ctx.fillRect(cx, townY - h * 0.155, w * 0.022, h * 0.155);
  ctx.fillStyle = mix("#8d5340", sky, 0.3);
  ctx.beginPath();
  ctx.moveTo(cx - w * 0.006, townY - h * 0.155);
  ctx.lineTo(cx + w * 0.011, townY - h * 0.2);
  ctx.lineTo(cx + w * 0.028, townY - h * 0.155);
  ctx.closePath();
  ctx.fill();

  // Water: the sky's colour, darker, with the town smeared down into it.
  const gw = ctx.createLinearGradient(0, horizon, 0, h);
  gw.addColorStop(0, mix(skyHi, "#12303f", 0.35));
  gw.addColorStop(1, "#0f2836");
  ctx.fillStyle = gw;
  ctx.fillRect(0, horizon, w, h - horizon);
  ctx.save();
  ctx.beginPath();
  ctx.rect(0, horizon, w, h - horizon);
  ctx.clip();
  ctx.globalAlpha = 0.4;
  ctx.scale(1, -1);
  ctx.drawImage(
    ctx.canvas,
    0,
    horizon - h * 0.16,
    w,
    h * 0.16,
    0,
    -horizon - h * 0.22,
    w,
    h * 0.22,
  );
  ctx.restore();
  ctx.globalAlpha = 1;
  // Ripple lines: short, brightest near the horizon, thinning downward.
  for (let i = 0; i < 70; i++) {
    const ry = horizon + (h - horizon) * hash1(seed + 30, i) ** 1.4;
    const rw = w * (0.03 + hash1(seed + 31, i) * 0.22);
    const rx = w * hash1(seed + 32, i) - rw / 2;
    ctx.fillStyle = `rgba(214,238,246,${0.1 + hash1(seed + 33, i) * 0.3})`;
    ctx.fillRect(rx, ry, rw, Math.max(1, h * 0.004));
  }
  ctx.restore();

  // The glyph header, in the reference's exact place: over the sky, white,
  // a smaller line above a bigger one.
  glyphRun(ctx, w * 0.12, h * 0.045, h * 0.048, 5, seed + 60, {
    color: "rgba(238,246,248,0.82)",
    weight: 0.1,
  });
  glyphRun(ctx, w * 0.1, h * 0.11, h * 0.1, 4, seed + 61, {
    color: "#f4fafb",
    halo: "rgba(40,70,90,0.5)",
    weight: 0.13,
  });
}

/**
 * THE VESSEL BILLBOARD — the reference's "alien/saucer-ship billboard":
 * landscape format, a hull hanging over a pale apron, a figure at the left
 * third for scale, glyph header across the top. Bebop's is an aether liner
 * over a ridge port.
 */
export function drawVesselPoster(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  seed = 2,
): void {
  const horizon = h * 0.7;
  const gs = ctx.createLinearGradient(0, 0, 0, horizon);
  gs.addColorStop(0, "#2b4658");
  gs.addColorStop(0.6, "#6d95a6");
  gs.addColorStop(1, "#d5e4e6");
  ctx.fillStyle = gs;
  ctx.fillRect(0, 0, w, h);
  // A distant skyline, deep in haze.
  ridge(ctx, w, horizon - h * 0.06, h * 0.03, seed + 1, "#3a5b6b", "#d5e4e6", 0.62, horizon);
  ctx.fillStyle = "rgba(70,100,116,0.5)";
  for (let i = 0; i < 26; i++) {
    const bx = w * hash1(seed + 10, i);
    ctx.fillRect(bx, horizon - h * (0.03 + hash1(seed + 11, i) * 0.09), w * 0.012, h * 0.12);
  }
  // Apron: a bright plane running to the horizon, with a scuffed grade.
  const ga = ctx.createLinearGradient(0, horizon, 0, h);
  ga.addColorStop(0, "#c8d5d6");
  ga.addColorStop(1, "#7d8d92");
  ctx.fillStyle = ga;
  ctx.fillRect(0, horizon, w, h - horizon);

  // The hull. A flattened ellipsoid, top-lit, with a dark belly, a bright
  // spine, a hard specular kick and a row of window lights.
  const hx = w * 0.6;
  const hy = h * 0.47;
  const hw = w * 0.42;
  const hh = h * 0.11;
  ctx.save();
  ctx.translate(hx, hy);
  ctx.scale(1, hh / hw);
  ctx.beginPath();
  ctx.arc(0, 0, hw, 0, Math.PI * 2);
  ctx.restore();
  ctx.fillStyle = keyFill(ctx, hx, hy - hh, hh * 3, "#e2ecec", "#2c3d47", Math.PI / 2 + 0.5);
  ctx.fill();
  // A dorsal blister and a fin.
  blob(ctx, hx + hw * 0.1, hy - hh * 0.9, hw * 0.24, hh * 0.85, "#eef5f4", "#61757e", 0.5);
  ctx.fillStyle = "#2b3b45";
  ctx.beginPath();
  ctx.moveTo(hx - hw * 0.75, hy);
  ctx.lineTo(hx - hw * 1.02, hy - hh * 1.5);
  ctx.lineTo(hx - hw * 0.5, hy - hh * 0.2);
  ctx.closePath();
  ctx.fill();
  // Belly shadow and the drive glow beneath it.
  blob(
    ctx,
    hx,
    hy + hh * 0.72,
    hw * 0.9,
    hh * 0.55,
    "rgba(18,28,36,0.75)",
    "rgba(18,28,36,0)",
    0.4,
  );
  blob(
    ctx,
    hx - hw * 0.2,
    hy + hh * 1.1,
    hw * 0.3,
    hh * 0.5,
    "rgba(120,240,255,0.7)",
    "rgba(120,240,255,0)",
    0.85,
  );
  // Window run: a line of tiny warm lights, the scale cue.
  for (let i = 0; i < 22; i++) {
    const t = i / 21;
    ctx.fillStyle = `rgba(255,236,196,${0.5 + hash1(seed + 20, i) * 0.5})`;
    ctx.fillRect(
      hx - hw * 0.8 + t * hw * 1.5,
      hy - hh * 0.06,
      Math.max(1, w * 0.006),
      Math.max(1, h * 0.008),
    );
  }
  // The figure at the left third, backlit, with a cast shadow toward camera.
  const fx = w * 0.19;
  const fy = horizon + h * 0.02;
  ctx.fillStyle = "rgba(30,44,54,0.35)";
  ctx.beginPath();
  ctx.ellipse(fx, fy + h * 0.005, w * 0.05, h * 0.012, 0, 0, Math.PI * 2);
  ctx.fill();
  const fg = keyFill(ctx, fx, fy - h * 0.1, h * 0.12, "#8fa3ab", "#1d2c36", 0.5);
  ctx.fillStyle = fg;
  ctx.beginPath();
  ctx.moveTo(fx - w * 0.022, fy);
  ctx.lineTo(fx - w * 0.026, fy - h * 0.12);
  ctx.lineTo(fx + w * 0.024, fy - h * 0.12);
  ctx.lineTo(fx + w * 0.02, fy);
  ctx.closePath();
  ctx.fill();
  blob(ctx, fx, fy - h * 0.145, w * 0.019, h * 0.028, "#9fb3ba", "#26343d", 0.4);

  // Everything above is FORM; soften it, then lay the two hard marks a
  // photograph keeps — the hull's specular kick and the figure's rim.
  soften(ctx, Math.max(1, Math.round(h * 0.008)));
  ctx.fillStyle = "rgba(255,255,255,0.72)";
  ctx.beginPath();
  ctx.ellipse(hx + hw * 0.18, hy - hh * 0.58, hw * 0.3, hh * 0.08, -0.06, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "rgba(232,248,252,0.85)";
  ctx.fillRect(fx + w * 0.019, fy - h * 0.145, Math.max(1, w * 0.005), h * 0.14);

  glyphRun(ctx, w * 0.06, h * 0.04, h * 0.1, 6, seed + 40, {
    color: "rgba(244,250,252,0.9)",
    halo: "rgba(20,40,56,0.45)",
    weight: 0.11,
  });
  glyphRun(ctx, w * 0.07, h * 0.16, h * 0.05, 8, seed + 41, {
    color: "rgba(226,240,246,0.62)",
    weight: 0.1,
  });
}

/**
 * THE PORTRAIT — the reference's ad wall carries poster strips with faces
 * on them. A face is the hardest thing on this list and the most valuable,
 * because a viewer forgives an approximate ship and never forgives a
 * missing person. Built the way a photographer lights one: a key at 3/4, a
 * rim on the shadow side, a fill off the background, hair as a mass with a
 * specular band, and everything soft-edged.
 */
export function drawPortraitPoster(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  seed = 3,
): void {
  const warm = hash1(seed, 1) > 0.5;
  const bg1 = warm ? "#2b1219" : "#0d222b";
  const bg2 = warm ? "#8d2f3c" : "#1d5f70";
  const gb = ctx.createLinearGradient(w * 0.9, 0, 0, h);
  gb.addColorStop(0, mix(bg1, bg2, 0.55));
  gb.addColorStop(1, mix(bg1, "#05070a", 0.6));
  ctx.fillStyle = gb;
  ctx.fillRect(0, 0, w, h);
  // Studio falloff: the light on the backdrop, off-centre and behind the
  // subject's shadow side, which is where a photographer puts it.
  blob(
    ctx,
    w * 0.72,
    h * 0.3,
    w * 0.62,
    h * 0.5,
    warm ? "rgba(226,132,96,0.5)" : "rgba(104,198,220,0.45)",
    "rgba(0,0,0,0)",
    0.95,
  );

  const skinLit = warm ? "#f6d3ae" : "#e6cdb6";
  const skinMid = warm ? "#b87f5f" : "#9c7c6c";
  const skinDark = warm ? "#3d1c1a" : "#221f28";
  const cx = w * 0.46;
  const cy = h * 0.4;
  const rx = w * 0.23;
  const ry = h * 0.185;
  const hair = mix(warm ? "#2a1410" : "#141b22", "#05070a", 0.35);

  // ---- FORMS. Everything in this block gets softened afterwards.
  // Shoulders, under the head, so the jaw overlaps them.
  ctx.fillStyle = keyFill(ctx, cx, h * 0.9, w * 0.5, mix(skinMid, "#232830", 0.6), "#0b1016", 3.6);
  ctx.beginPath();
  ctx.ellipse(cx - w * 0.02, h * 1.08, w * 0.5, h * 0.32, 0.05, 0, Math.PI * 2);
  ctx.fill();
  // Neck, in its own shadow — the jaw always shades the throat.
  ctx.fillStyle = keyFill(ctx, cx, cy + ry, rx, mix(skinMid, "#000000", 0.3), skinDark, 3.6);
  ctx.beginPath();
  ctx.ellipse(cx, cy + ry * 1.5, rx * 0.44, ry * 0.95, 0, 0, Math.PI * 2);
  ctx.fill();

  // The head: an ovoid with a hard key from the upper left and a deep
  // shadow side. A face with even light is a mask.
  ctx.save();
  ctx.beginPath();
  ctx.ellipse(cx, cy, rx, ry, 0.03, 0, Math.PI * 2);
  ctx.clip();
  ctx.fillStyle = keyFill(ctx, cx, cy, rx * 1.25, skinLit, skinDark, 3.55);
  ctx.fillRect(cx - rx, cy - ry, rx * 2, ry * 2);
  blob(
    ctx,
    cx - rx * 0.34,
    cy + ry * 0.02,
    rx * 0.5,
    ry * 0.44,
    "rgba(255,238,216,0.26)",
    "rgba(255,238,216,0)",
    0.9,
  );
  blob(
    ctx,
    cx + rx * 0.62,
    cy + ry * 0.3,
    rx * 0.66,
    ry * 0.8,
    "rgba(12,8,12,0.62)",
    "rgba(12,8,12,0)",
    0.9,
  );
  // Brow ridge: one soft horizontal shadow across the upper face. It is the
  // feature that most says "skull under skin".
  blob(
    ctx,
    cx - rx * 0.05,
    cy - ry * 0.3,
    rx * 0.72,
    ry * 0.13,
    "rgba(58,30,28,0.5)",
    "rgba(58,30,28,0)",
    0.85,
  );
  // Eyes: socket, brow, lid, iris. Four soft marks, no outlines.
  for (const sx of [-0.4, 0.28] as const) {
    blob(
      ctx,
      cx + rx * sx,
      cy - ry * 0.1,
      rx * 0.26,
      ry * 0.17,
      "rgba(46,24,26,0.55)",
      "rgba(46,24,26,0)",
      0.75,
    );
    ctx.fillStyle = "rgba(24,16,20,0.9)";
    ctx.beginPath();
    ctx.ellipse(cx + rx * sx, cy - ry * 0.07, rx * 0.115, ry * 0.06, 0, 0, Math.PI * 2);
    ctx.fill();
    // The upper lid: a darker crescent over the eye, which is what makes an
    // eye an eye rather than a bead.
    ctx.fillStyle = "rgba(30,18,20,0.75)";
    ctx.beginPath();
    ctx.ellipse(cx + rx * sx, cy - ry * 0.15, rx * 0.14, ry * 0.055, 0, 0, Math.PI);
    ctx.fill();
    ctx.fillStyle = "rgba(58,32,30,0.5)";
    ctx.beginPath();
    ctx.ellipse(
      cx + rx * sx,
      cy - ry * 0.28,
      rx * 0.15,
      ry * 0.032,
      -0.1 * Math.sign(sx),
      0,
      Math.PI * 2,
    );
    ctx.fill();
  }
  // Nose: shadow down the shadow side, lit tip, one nostril note.
  blob(
    ctx,
    cx + rx * 0.04,
    cy + ry * 0.16,
    rx * 0.13,
    ry * 0.3,
    "rgba(50,24,22,0.5)",
    "rgba(50,24,22,0)",
    0.8,
  );
  blob(
    ctx,
    cx - rx * 0.09,
    cy + ry * 0.24,
    rx * 0.08,
    ry * 0.1,
    "rgba(255,236,214,0.55)",
    "rgba(255,236,214,0)",
    0.7,
  );
  // Mouth: an upper lip in shadow, a lit lower one.
  ctx.fillStyle = warm ? "rgba(128,44,48,0.72)" : "rgba(96,52,54,0.68)";
  ctx.beginPath();
  ctx.ellipse(cx - rx * 0.05, cy + ry * 0.5, rx * 0.19, ry * 0.062, 0.04, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "rgba(240,186,158,0.4)";
  ctx.beginPath();
  ctx.ellipse(cx - rx * 0.1, cy + ry * 0.56, rx * 0.1, ry * 0.03, 0.04, 0, Math.PI * 2);
  ctx.fill();
  // Jaw shadow up from the neck.
  blob(ctx, cx, cy + ry * 0.95, rx * 0.8, ry * 0.3, "rgba(20,10,14,0.5)", "rgba(20,10,14,0)", 0.85);
  ctx.restore();

  // Hair: a mass that breaks the skull's silhouette, plus a fall past the
  // jaw. Drawn outside the clip on purpose — an unbroken ovoid is an egg.
  ctx.fillStyle = keyFill(ctx, cx, cy - ry * 0.7, rx * 1.2, mix(hair, "#6d7d8c", 0.26), hair, 3.7);
  ctx.beginPath();
  ctx.ellipse(
    cx - rx * 0.05,
    cy - ry * 0.58,
    rx * 1.1,
    ry * 0.8,
    -0.08,
    Math.PI * 0.92,
    Math.PI * 2.14,
  );
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(cx + rx * 0.94, cy + ry * 0.18, rx * 0.26, ry * 0.72, -0.26, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(cx - rx * 0.97, cy + ry * 0.06, rx * 0.24, ry * 0.6, 0.2, 0, Math.PI * 2);
  ctx.fill();

  // ---- THE SOFTENING. Everything above becomes photographic here.
  soften(ctx, Math.max(1, Math.round(w * 0.008)));

  // ---- SHARP PASS. A photograph's hard marks: strands, rim, catchlights.
  for (let i = 0; i < 14; i++) {
    const a = Math.PI * (0.98 + hash1(seed + 60, i) * 1.1);
    ctx.strokeStyle = `rgba(${warm ? "236,196,158" : "176,204,222"},${0.06 + hash1(seed + 61, i) * 0.16})`;
    ctx.lineWidth = Math.max(0.6, w * 0.004);
    ctx.beginPath();
    ctx.ellipse(
      cx - rx * 0.05,
      cy - ry * (0.3 + hash1(seed + 62, i) * 0.3),
      rx * (0.9 + hash1(seed + 63, i) * 0.3),
      ry * 0.95,
      -0.08,
      a,
      a + 0.5,
    );
    ctx.stroke();
  }
  ctx.save();
  ctx.globalCompositeOperation = "lighter";
  // THE RIM. A short crescent low on the shadow side of the hair and jaw —
  // not a halo. The first pass ran the arc a full 130° around the skull and
  // it read as a headset, which is the failure mode of every rim light
  // drawn as a circle instead of as the sliver of a turning surface.
  const rimColor = warm ? "255,206,158" : "156,230,248";
  for (const [ex, ey, erx, ery, a0, a1, str] of [
    [cx - rx * 0.05, cy - ry * 0.44, rx * 1.16, ry * 0.98, -0.55, 0.62, 0.72],
    [cx - w * 0.02, h * 1.08, w * 0.5, h * 0.32, -0.85, -0.2, 0.4],
  ] as const) {
    for (let k = 0; k < 3; k++) {
      ctx.strokeStyle = `rgba(${rimColor},${(str * (0.3 - k * 0.08)).toFixed(3)})`;
      ctx.lineWidth = Math.max(0.8, w * 0.005 * (1 + k * 1.8));
      ctx.beginPath();
      ctx.ellipse(ex, ey, erx, ery, -0.08, a0, a1);
      ctx.stroke();
    }
  }
  // Catchlights: the two hardest, smallest, brightest marks in the frame.
  for (const sx of [-0.4, 0.28] as const) {
    ctx.fillStyle = "rgba(250,250,244,0.8)";
    ctx.beginPath();
    ctx.arc(cx + rx * (sx - 0.04), cy - ry * 0.1, Math.max(0.6, rx * 0.02), 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();

  // The strapline, cut across the bottom.
  ctx.fillStyle = "rgba(6,10,14,0.6)";
  ctx.fillRect(0, h * 0.81, w, h * 0.19);
  glyphRun(ctx, w * 0.08, h * 0.845, h * 0.1, 5, seed + 50, {
    color: warm ? "#ffd9c2" : "#d6f2fa",
    weight: 0.12,
  });
}

/**
 * THE BRAND PANEL — the reference's Coca-Cola parody: a near-white field
 * with one saturated band, a brush wordmark, and a bottle. It is also where
 * the reference's low-sat WHITE census (14.6% of all emissive pixels) comes
 * from, and where its Y≥200 population lives. Ours advertises a bottled
 * aether tonic, which is the same idea in Bebop's own world.
 */
export function drawBrandPanel(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  seed = 4,
  family: "a" | "b" = "b",
): void {
  // Explicit, never a coin-flip on the seed. The accent census is a LAW —
  // the reference puts 58% of its emissive pixels in family A and 22% in B
  // — and a district that decides its accent by hashing seeds cannot hold a
  // ratio. The first pass let the hash choose and landed family A at 0.3%
  // of emissive against the reference's 22.5% on the same classifier.
  const accent = family === "a" ? ACCENT_A : ACCENT_B;
  ctx.fillStyle = "#eef3f4";
  ctx.fillRect(0, 0, w, h);
  // The brand band, with a swept lower edge — a logo block, not a stripe.
  ctx.fillStyle = accent;
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(w, 0);
  ctx.lineTo(w, h * 0.46);
  for (let x = w; x >= 0; x -= 4) {
    ctx.lineTo(x, h * (0.44 + 0.08 * Math.sin((x / w) * 3.1 + 1.2)));
  }
  ctx.closePath();
  ctx.fill();
  // Wordmark in white over the band; a second, smaller one under it in the
  // accent — the reference's exact two-line layout.
  glyphRun(ctx, w * 0.08, h * 0.06, h * 0.28, 4, seed + 10, {
    color: "#ffffff",
    halo: "rgba(90,10,26,0.28)",
    weight: 0.15,
    wobble: 0.14,
  });
  glyphRun(ctx, w * 0.16, h * 0.58, h * 0.16, 5, seed + 11, {
    color: accent,
    weight: 0.13,
    wobble: 0.12,
  });
  // The bottle: a lit glass form with a meniscus and a hard specular strip.
  const bx = w * 0.74;
  const by = h * 0.58;
  const bw = w * 0.16;
  const bh = h * 0.36;
  ctx.fillStyle = keyFill(
    ctx,
    bx,
    by,
    bw,
    mix(accent, "#ffffff", 0.35),
    mix(accent, "#000000", 0.45),
    3.4,
  );
  ctx.beginPath();
  ctx.moveTo(bx - bw * 0.5, by + bh * 0.5);
  ctx.quadraticCurveTo(bx - bw * 0.62, by - bh * 0.1, bx - bw * 0.22, by - bh * 0.34);
  ctx.lineTo(bx - bw * 0.2, by - bh * 0.5);
  ctx.lineTo(bx + bw * 0.2, by - bh * 0.5);
  ctx.lineTo(bx + bw * 0.22, by - bh * 0.34);
  ctx.quadraticCurveTo(bx + bw * 0.62, by - bh * 0.1, bx + bw * 0.5, by + bh * 0.5);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = "rgba(255,255,255,0.75)";
  ctx.fillRect(bx - bw * 0.34, by - bh * 0.22, Math.max(1, bw * 0.1), bh * 0.62);
  ctx.fillStyle = "rgba(255,255,255,0.3)";
  ctx.fillRect(bx + bw * 0.26, by - bh * 0.14, Math.max(1, bw * 0.06), bh * 0.5);
  // Condensation.
  for (let i = 0; i < 18; i++) {
    ctx.fillStyle = `rgba(255,255,255,${0.2 + hash1(seed + 20, i) * 0.4})`;
    ctx.beginPath();
    ctx.arc(
      bx + (hash1(seed + 21, i) - 0.5) * bw * 0.8,
      by + (hash1(seed + 22, i) - 0.4) * bh * 0.8,
      Math.max(0.6, w * 0.006 * hash1(seed + 23, i)),
      0,
      Math.PI * 2,
    );
    ctx.fill();
  }
}

/**
 * THE FIGURE STRIP — the reference's stacked "trooper poster strips": three
 * cells, each a different key colour, each holding one to three backlit
 * figures. Tiny in frame and unreadable, but the presence of PEOPLE in the
 * advertising is most of what makes a night canyon feel inhabited.
 */
export function drawFigureStrip(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  seed = 5,
): void {
  const cells = 3;
  // Three keys, each cell gets a DIFFERENT one — the reference's strips are
  // three visibly unlike stills, and a stack of three near-identical cells
  // is how a "poster strip" becomes a wallpaper swatch.
  const keys: readonly (readonly [string, string, string])[] = [
    ["#d4661e", "#3a1208", "255,214,150"], // sodium
    ["#a8203f", "#1c050d", "255,168,180"], // crimson
    ["#1c7d92", "#04202c", "170,238,252"], // aether
  ];
  const first = Math.floor(hash1(seed, 0) * 3) % 3;
  const second = (first + 1 + Math.floor(hash1(seed, 1) * 2)) % 3;
  // 0 + 1 + 2 = 3, and the first two are distinct, so the third is what's left.
  const pick = [first, second, 3 - first - second];
  for (let c = 0; c < cells; c++) {
    const y0 = (h / cells) * c;
    const ch = h / cells - h * 0.012;
    const key = keys[pick[c] ?? c] ?? keys[0];
    if (!key) continue;
    // Composition varies per cell: a wide group, a mid two-shot, a close
    // single. Same reason — three cells, three shots.
    const shot = (c + Math.floor(hash1(seed + 2, c) * 3)) % 3;
    const n = shot === 0 ? 3 : shot === 1 ? 2 : 1;
    const zoom = shot === 0 ? 0.62 : shot === 1 ? 0.86 : 1.5;
    const lightX = 0.2 + hash1(seed + 3, c) * 0.6;
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, y0, w, ch);
    ctx.clip();
    const g = ctx.createLinearGradient(0, y0, w * 0.3, y0 + ch);
    g.addColorStop(0, key[1]);
    g.addColorStop(0.5, key[0]);
    g.addColorStop(1, mix(key[1], "#000000", 0.45));
    ctx.fillStyle = g;
    ctx.fillRect(0, y0, w, ch);
    // A blown-out backlight, placed off-centre per cell.
    blob(
      ctx,
      w * lightX,
      y0 + ch * (0.5 + hash1(seed + 4, c) * 0.3),
      w * 0.4,
      ch * 0.55,
      "rgba(255,240,212,0.72)",
      "rgba(255,240,212,0)",
      0.95,
    );
    // Ground shadow band.
    ctx.fillStyle = "rgba(4,6,9,0.45)";
    ctx.fillRect(0, y0 + ch * 0.88, w, ch * 0.12);

    // One path per figure, reused for the fill and for the rim — a rim that
    // is stroked along a DIFFERENT curve from the body reads as an outline
    // floating beside a silhouette, which is what the first pass did.
    const figures: Array<{ path: Path2D; fx: number; scale: number; depth: number }> = [];
    for (let i = 0; i < n; i++) {
      const depth = n === 1 ? 0 : i / (n - 1);
      const spread = n === 1 ? 0.5 : 0.14 + (i / (n - 1)) * 0.7;
      const fx = w * (spread + (hash1(seed + 6, c * 4 + i) - 0.5) * 0.12);
      const scale = ch * zoom * (1 - depth * 0.22);
      const fy = y0 + ch * (1.02 - depth * 0.07);
      const tilt = (hash1(seed + 7, c * 4 + i) - 0.5) * 0.28;
      const path = new Path2D();
      // Torso with real shoulders, and a head set on the neck at a tilt.
      path.moveTo(fx - scale * 0.21, fy);
      path.quadraticCurveTo(
        fx - scale * 0.27,
        fy - scale * 0.5,
        fx - scale * 0.15,
        fy - scale * 0.66,
      );
      path.quadraticCurveTo(fx - scale * 0.09, fy - scale * 0.75, fx, fy - scale * 0.75);
      path.quadraticCurveTo(
        fx + scale * 0.09,
        fy - scale * 0.75,
        fx + scale * 0.15,
        fy - scale * 0.66,
      );
      path.quadraticCurveTo(fx + scale * 0.27, fy - scale * 0.5, fx + scale * 0.21, fy);
      path.closePath();
      path.ellipse(
        fx + Math.sin(tilt) * scale * 0.1,
        fy - scale * 0.86,
        scale * 0.1,
        scale * 0.125,
        tilt,
        0,
        Math.PI * 2,
      );
      // Depth lifts a figure toward the key, but only a little: mixed all
      // the way, the far figures dissolved into the backdrop and left their
      // rim lights behind as free-floating neon outlines.
      ctx.fillStyle = mix("#0b0810", key[0], 0.05 + depth * 0.16);
      ctx.fill(path);
      figures.push({ path, fx, scale, depth });
    }
    // Soften the whole cell: silhouettes stop being cut-outs.
    soften(ctx, Math.max(1, Math.round(w * 0.01)), 0, y0, w, ch);

    // Sharp pass: the rim, stroked along the body's own edge and clipped to
    // the half of the figure the backlight is behind.
    ctx.save();
    ctx.globalCompositeOperation = "lighter";
    for (const f of figures) {
      const side = f.fx / w < lightX ? 1 : -1;
      ctx.save();
      ctx.beginPath();
      ctx.rect(side > 0 ? f.fx : f.fx - f.scale * 0.4, y0, f.scale * 0.4, ch);
      ctx.clip();
      // Three passes: a hot filament, a bloom and a wash. One pass is a
      // pen line; three are a light source seen edge-on.
      for (let k = 0; k < 3; k++) {
        ctx.strokeStyle = `rgba(${key[2]},${((0.45 - f.depth * 0.14) * (0.42 - k * 0.13)).toFixed(3)})`;
        ctx.lineWidth = Math.max(0.7, f.scale * 0.016 * (1 + k * 4));
        ctx.stroke(f.path);
      }
      ctx.restore();
    }
    ctx.restore();
    ctx.restore();
    // Gutter.
    ctx.fillStyle = "#05080a";
    ctx.fillRect(0, y0 + ch, w, h * 0.012);
    // A caption strip on one cell in two — printed matter has captions.
    if (hash1(seed + 9, c) > 0.45) {
      glyphRun(ctx, w * 0.07, y0 + ch * 0.06, ch * 0.13, 3, seed + 30 + c, {
        color: `rgba(${key[2]},0.85)`,
        weight: 0.13,
      });
    }
  }
}

/**
 * THE HANGING BANNER — the hero. The reference's is "double-faced, red/cyan
 * diagonal split, white brush-glyphs, visible box frame + drooping cable
 * bundle", and it is the one fixture carrying BOTH accent families, which
 * §e names as a law. Ours is the same construction with Bebop's hues, its
 * script laid in a vertical column, and honest fabric: weave, a sagging
 * lower edge and a frayed hem.
 */
export function drawHeroBanner(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  seed = 6,
): void {
  ctx.fillStyle = INK;
  ctx.fillRect(0, 0, w, h);
  // The diagonal: teal is the FIELD and crimson the wedge cut out of it.
  // The reference's hero reads majority-cyan (its cyan patch means Y173
  // against its red patch's Y105) and it is the largest single emissive
  // object in the plate — which makes it, on its own, most of the accent
  // census. Painting it the other way round is how family A collapsed.
  ctx.fillStyle = ACCENT_A;
  ctx.fillRect(0, 0, w, h);
  ctx.fillStyle = ACCENT_B;
  ctx.beginPath();
  ctx.moveTo(w, 0);
  ctx.lineTo(w, h);
  ctx.lineTo(w * 0.18, h);
  ctx.closePath();
  ctx.fill();
  // Each field gets its own vertical key: a lightbox is brightest at its
  // tubes, and the reference's banner runs Y209 at the top and Y120 low.
  // Gentle. A lightbox IS brighter at its tubes and dimmer at its sill, but
  // the first pass ran the sill to 42% black and the field then arrived on
  // screen at Y101 against the reference banner's measured Y161 — the whole
  // "our neon looks washed out" impression in one gradient. §e's own number
  // is that sign interiors run 150–220; whatever a fixture's own shading
  // takes off that, the fog then takes off again.
  const gt = ctx.createLinearGradient(0, 0, 0, h);
  gt.addColorStop(0, "rgba(255,255,255,0.22)");
  gt.addColorStop(0.4, "rgba(255,255,255,0.03)");
  gt.addColorStop(1, "rgba(0,18,26,0.2)");
  ctx.fillStyle = gt;
  ctx.fillRect(0, 0, w, h);
  // Weave: the fabric this is printed on, at the scale the camera can see.
  const weave = makeFbm(seed + 3, [w / 3, w / 9]);
  const img = ctx.getImageData(0, 0, w, h);
  const d = img.data;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      const k =
        0.97 + 0.05 * Math.sin(x * 1.9) * Math.sin(y * 1.7) + (weave(x / w, y / h) - 0.5) * 0.12;
      d[i] = clamp255((d[i] ?? 0) * k);
      d[i + 1] = clamp255((d[i + 1] ?? 0) * k);
      d[i + 2] = clamp255((d[i + 2] ?? 0) * k);
    }
  }
  ctx.putImageData(img, 0, 0);
  // The script: one column of big white brush glyphs down the banner, the
  // way the reference sets it. Five forms, not twenty scratches.
  glyphRun(ctx, w * 0.16, h * 0.06, w * 0.55, 5, seed + 20, {
    color: "#f6fbfc",
    halo: "rgba(12,30,40,0.35)",
    weight: 0.14,
    vertical: true,
    wobble: 0.13,
  });
  // Weathering: rain streaks off the top rail, and a stained hem.
  for (let i = 0; i < 14; i++) {
    ctx.fillStyle = `rgba(8,16,20,${0.08 + hash1(seed + 40, i) * 0.14})`;
    ctx.fillRect(
      w * hash1(seed + 41, i),
      0,
      Math.max(1, w * 0.012),
      h * (0.1 + hash1(seed + 42, i) * 0.5),
    );
  }
  const hem = ctx.createLinearGradient(0, h * 0.86, 0, h);
  hem.addColorStop(0, "rgba(10,26,36,0)");
  hem.addColorStop(1, "rgba(10,26,36,0.7)");
  ctx.fillStyle = hem;
  ctx.fillRect(0, h * 0.86, w, h * 0.14);
  // Frayed hem: a ragged bottom edge, because the reference's is ragged.
  ctx.fillStyle = INK;
  for (let x = 0; x < w; x += 2) {
    ctx.fillRect(x, h - h * 0.03 * hash1(seed + 50, x), 2, h * 0.03);
  }
  // The box frame, drawn into the sheet so the fixture needs no extra mesh
  // for its bezel.
  ctx.strokeStyle = "#141b20";
  ctx.lineWidth = Math.max(2, w * 0.035);
  ctx.strokeRect(0, 0, w, h);
}

/**
 * FLUSH WALL NEON — a real tube, not a lightbox: a dark backing plate with
 * a glass run bent into a glyph, drawn as a wide soft pass, a saturated
 * pass and a near-white core. Neon is three strokes of the same path.
 */
export function drawNeonPanel(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  seed = 7,
  family: "a" | "b" = "a",
): void {
  const teal = family === "a";
  const hue = teal ? ACCENT_A : ACCENT_B;
  ctx.fillStyle = "#0a0f13";
  ctx.fillRect(0, 0, w, h);
  // Backing plate with a lit halo already burnt into it — the wall around a
  // neon is never dark.
  blob(
    ctx,
    w * 0.5,
    h * 0.5,
    w * 0.6,
    h * 0.6,
    teal ? "rgba(30,140,160,0.5)" : "rgba(150,36,56,0.45)",
    "rgba(0,0,0,0)",
    0.95,
  );
  // Three passes over the same glyphs: bloom, tube, core.
  glyphRun(ctx, w * 0.1, h * 0.22, h * 0.5, 3, seed + 5, {
    color: teal ? "rgba(40,190,210,0.30)" : "rgba(220,60,86,0.30)",
    weight: 0.4,
  });
  glyphRun(ctx, w * 0.1, h * 0.22, h * 0.5, 3, seed + 5, {
    color: hue,
    weight: 0.16,
  });
  // 0.92 put eight pixels of the district over the "no clipped whites"
  // line once a kiosk's own spill stacked on a hatch. A tube core is bright,
  // not blown.
  glyphRun(ctx, w * 0.1, h * 0.22, h * 0.5, 3, seed + 5, {
    color: "rgba(238,252,252,0.8)",
    weight: 0.062,
  });
  // Mounting hardware: the standoffs and the transformer can.
  ctx.fillStyle = "rgba(6,10,13,0.85)";
  ctx.fillRect(w * 0.04, h * 0.04, w * 0.02, h * 0.92);
  ctx.fillRect(w * 0.94, h * 0.04, w * 0.02, h * 0.92);
}

/**
 * THE INTERIOR-LIT WINDOW BAND. The audit's one named "never" is a lit
 * window grid, and its one permitted window feature is "5 glazed panels
 * with graded interior light and dark occluder silhouettes" — interior-lit
 * BOXES, not emissive rectangles. So: bays of unequal width at unequal
 * heights, a third of them dark, each lit from a source INSIDE the room
 * (bright at the ceiling, falling to the sill) with furniture and people
 * blocking it.
 */
export function drawInteriorBand(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  seed = 8,
): void {
  ctx.fillStyle = "#0d141a";
  ctx.fillRect(0, 0, w, h);
  let x = w * 0.02;
  let bay = 0;
  while (x < w * 0.98) {
    const bw = w * (0.045 + hash1(seed, bay) * 0.075);
    const v = hash1(seed + 1, bay);
    // Vacancy: a third of any real frontage is dark at any hour, and it is
    // the dark bays that stop a run reading as one lit panel with a mask.
    if (v > 0.33 && bw > w * 0.055) {
      const bh = h * (0.42 + hash1(seed + 2, bay) * 0.4);
      const by = h * 0.12 + (h * 0.76 - bh) * hash1(seed + 3, bay);
      // The room: bright at the ceiling, falling to the sill. Warm or cool
      // by tenancy, never the same twice in a row.
      const warm = hash1(seed + 4, bay) > 0.62;
      const g = ctx.createLinearGradient(0, by, 0, by + bh);
      g.addColorStop(0, warm ? "#f0cf95" : "#accfd9");
      g.addColorStop(0.45, warm ? "#a97b48" : "#4f8898");
      g.addColorStop(1, warm ? "#3a281c" : "#1c3742");
      ctx.fillStyle = g;
      ctx.fillRect(x, by, bw * 0.86, bh);
      // Occluders: a blind pulled part-way, a mullion, a figure at the glass.
      ctx.fillStyle = "rgba(6,10,13,0.9)";
      ctx.fillRect(x, by, bw * 0.86, bh * (0.1 + v * 0.28));
      if (bw > w * 0.11) ctx.fillRect(x + bw * 0.43 - 1, by, 2, bh);
      if (v > 0.7) {
        const fx = x + bw * (0.15 + hash1(seed + 5, bay) * 0.5);
        ctx.fillRect(fx, by + bh * 0.45, bw * 0.1, bh * 0.55);
        ctx.beginPath();
        ctx.arc(fx + bw * 0.05, by + bh * 0.4, bw * 0.055, 0, Math.PI * 2);
        ctx.fill();
      }
      // A sill line and the grime the glass carries.
      ctx.fillStyle = "rgba(4,8,11,0.75)";
      ctx.fillRect(x - bw * 0.03, by + bh, bw * 0.92, Math.max(1, h * 0.02));
      ctx.fillStyle = "rgba(140,170,180,0.06)";
      ctx.fillRect(x, by, bw * 0.86, bh * 0.5);
    }
    x += bw * (1.05 + hash1(seed + 6, bay) * 0.5);
    bay++;
  }
}

/**
 * FACADE MASSING — the surface of an unlit tower. The audit is precise
 * about what is allowed on one: "distant windows are 1–2 px specks below
 * Y60; at most rare small clusters (peak Y ~170)". So this sheet is
 * near-black, with structure that only exists as a value break, and specks
 * whose brightness is chosen so that they LAND below Y60 after the scene's
 * fog lifts them — the previous sheet's window rows were 40× too dense and
 * 3× too bright, and they are the whole reason the district read as
 * wallpaper.
 */
export function drawFacade(ctx: CanvasRenderingContext2D, w: number, h: number, seed = 9): void {
  const fbm = makeFbm(seed, [2, 6, 19]);
  // A fine aggregate tooth on top of the slow drift. Without it the sheet is
  // three octaves of low-frequency noise, which at 1:1 on a 40 m wall reads
  // as smooth gradients — i.e. as flat colour, which is this project's
  // most-repeated failure. Concrete has grit at the millimetre scale and the
  // camera gets within a few metres of these walls.
  const tooth = makeFbm(seed + 501, [96, 224]);
  const stain = makeFbm(seed + 733, [4, 13]);
  const img = ctx.createImageData(w, h);
  const d = img.data;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      // Concrete: a slow value drift plus a fine tooth, all of it dark.
      const n = fbm(x / w, y / h) * 0.82 + tooth(x / w, y / h) * 0.18;
      // 15+12n crushed the near massing to Y12–21 and dragged the frame's
      // 10th-percentile luma to 17 against §e's 20–28 window. The law is
      // that distant blacks never fall under the ~26 fog floor and only
      // NEAR-field unlit surfaces may reach ~10 — this sheet is both, so it
      // sits just high enough that the near wall reads Y~27 (ΔY 22 against
      // the Y49 haze, inside the ≥20 foreground-separation bar) and the fog
      // lifts everything further off it.
      let v = 26 + n * 14;
      // Panel seams and a string course every few storeys: structure read
      // purely as a value break, the way a silhouette reads it.
      // Barely there, and deliberately. World-scaled, these seams land ~1.7 m
      // apart; at the old 0.55/0.70 multipliers they read at 1:1 as a grid of
      // large tiles over every building in the canyon — the second time a
      // regular grid has been the loudest artefact in this district. A
      // concrete panel joint is a hairline, not a groove.
      if (y % 26 < 1.1) v *= 0.86;
      if (x % 34 < 1.1) v *= 0.92;
      if (y % 78 < 2) v *= 0.9;
      // Weathering: rain shadow under the string courses, and the vertical
      // streaks that run off every horizontal on a real facade. Irregular,
      // so the eye reads dirt rather than a second grid.
      v *= 0.86 + stain(x / w, y / h) * 0.3;
      if (y % 78 < 16) v *= 0.94 + (y % 78) * 0.005;
      d[i] = clamp255(v * 0.9);
      d[i + 1] = clamp255(v * 1.02);
      d[i + 2] = clamp255(v * 1.14);
      d[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  // Specks. Sparse — one per ~1400 texels — and dim.
  const count = Math.round((w * h) / 1400);
  for (let i = 0; i < count; i++) {
    const sx = hash1(seed + 10, i) * w;
    const sy = hash1(seed + 11, i) * h;
    const v = hash1(seed + 12, i);
    if (v > 0.955) {
      // A rare small cluster: three or four lit rooms on one landing.
      for (let k = 0; k < 4; k++) {
        ctx.fillStyle = `rgba(158,142,104,${0.4 + hash1(seed + 13, i * 4 + k) * 0.3})`;
        ctx.fillRect(sx + k * 3.2, sy + (k % 2) * 3, 2, 1.6);
      }
    } else if (v > 0.62) {
      ctx.fillStyle = `rgba(96,110,116,${0.2 + v * 0.22})`;
      ctx.fillRect(sx, sy, 1.4, 1.2);
    }
  }
}

// ---------------------------------------------------------------------------
// Atmosphere sheets

/**
 * A haze patch: the anchored glow the audit says IS the look ("haze
 * luminance is the glow medium — brighter around/above signage"). Not a
 * clean radial — a radial broken by low-frequency noise, so a hundred of
 * them never read as a hundred circles.
 */
export function hazePatch(t: Tracker, color: string, seed = 1): THREE.CanvasTexture {
  const n = 96;
  const s = sheet(n, n);
  const g = s.ctx.createRadialGradient(n / 2, n / 2, 1, n / 2, n / 2, n / 2);
  g.addColorStop(0, color);
  g.addColorStop(0.45, color);
  g.addColorStop(1, "rgba(0,0,0,0)");
  s.ctx.fillStyle = g;
  s.ctx.fillRect(0, 0, n, n);
  const fbm = makeFbm(seed, [2, 5, 11]);
  const img = s.ctx.getImageData(0, 0, n, n);
  const d = img.data;
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      const i = (y * n + x) * 4;
      const k = 0.45 + fbm(x / n, y / n) * 1.1;
      d[i] = clamp255((d[i] ?? 0) * k);
      d[i + 1] = clamp255((d[i + 1] ?? 0) * k);
      d[i + 2] = clamp255((d[i + 2] ?? 0) * k);
      d[i + 3] = clamp255((d[i + 3] ?? 0) * Math.min(1, k));
    }
  }
  s.ctx.putImageData(img, 0, 0);
  return texture(s.canvas, t);
}

/**
 * A haze CURTAIN: a full-width sheet of fog hung across the canyon, denser
 * at the bottom, torn at the top, with a couple of brighter cells where the
 * signage is. Four or five of these stacked in depth is what makes distant
 * towers dissolve instead of merely darken — the reference's towers are
 * barely visible, and exponential fog alone cannot do that without eating
 * the near field too.
 */
export function hazeCurtain(t: Tracker, color: string, seed = 1): THREE.CanvasTexture {
  const w = 256;
  const h = 128;
  const s = sheet(w, h);
  const fbm = makeFbm(seed, [2, 5, 13, 29]);
  const img = s.ctx.createImageData(w, h);
  const d = img.data;
  const rgb = color.match(/\d+/g)?.map(Number) ?? [40, 56, 66];
  for (let y = 0; y < h; y++) {
    const v = y / h;
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      // Dense low, thinning up, torn by fbm; and never a hard edge at the
      // sheet border, or the curtain becomes a visible pane.
      const body = Math.max(0, 1 - (1 - v) ** 1.5) * 0.75 + 0.25;
      const tear = 0.35 + fbm(x / w, v) * 1.05;
      const edge = Math.min(1, Math.min(x, w - x) / (w * 0.12));
      const a = Math.max(0, Math.min(1, body * tear * edge));
      d[i] = rgb[0] ?? 40;
      d[i + 1] = rgb[1] ?? 56;
      d[i + 2] = rgb[2] ?? 66;
      d[i + 3] = clamp255(a * 255);
    }
  }
  s.ctx.putImageData(img, 0, 0);
  return texture(s.canvas, t);
}

/**
 * THE DECK SOFFIT — the Understreet's ceiling, and its "sky".
 *
 * The reference has an open sky band whose luma runs 60→48→41 across the
 * frame and which carries a third of every plate. Bebop's canyon is decked
 * over, so the deck underside plays that part, and a flat colour plays it
 * badly: measured, a plain-coloured deck delivered a dead Y10 across the
 * top two rows of the frame against the reference's Y40/Y47, which was the
 * single biggest luma hole in the district.
 *
 * So the soffit is built like a real one — transverse girders, longitudinal
 * beams, coffered bays, service runs and rust bleed — at a mean bright
 * enough that the scene's own fog lifts it into the band. Structure the eye
 * can read at 30 m up, and value the histogram can count.
 */
export function deckSoffit(t: Tracker, seed = 5): THREE.CanvasTexture {
  const n = 256;
  const s = sheet(n, n);
  const grime = makeFbm(seed, [3, 9, 27]);
  const tooth = makeFbm(seed + 11, [48, 128]);
  const img = s.ctx.createImageData(n, n);
  const d = img.data;
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      const i = (y * n + x) * 4;
      // Coffered bays: the pan between the beams is lighter than its frame.
      const bx = x % 64;
      const by = y % 64;
      const inBeam = bx < 9 || by < 9;
      const edge = Math.min(bx, by, 64 - bx, 64 - by);
      let v = inBeam ? 150 : 192;
      // A pan is dished — it darkens into its own corners.
      if (!inBeam) v -= Math.max(0, 26 - edge) * 1.1;
      // Deep transverse girders every fourth bay, on the long axis.
      if (x % 256 < 16) v *= 0.74;
      v *= 0.8 + grime(x / n, y / n) * 0.36;
      v *= 0.92 + tooth(x / n, y / n) * 0.16;
      // Service run: a conduit bundle down one bay line, and its bracket.
      if (by > 26 && by < 32) v *= 0.72;
      if (by > 28 && by < 30) v *= 1.35;
      d[i] = clamp255(v * 0.98);
      d[i + 1] = clamp255(v);
      d[i + 2] = clamp255(v * 1.04);
      d[i + 3] = 255;
    }
  }
  s.ctx.putImageData(img, 0, 0);
  // Rust bleed at a few bay joints: the only warm note above the street.
  for (let i = 0; i < 12; i++) {
    const rx = hash1(seed + 30, i) * n;
    const ry = Math.floor(hash1(seed + 31, i) * 4) * 64;
    const g = s.ctx.createLinearGradient(0, ry, 0, ry + 34);
    g.addColorStop(0, "rgba(126,84,52,0.4)");
    g.addColorStop(1, "rgba(126,84,52,0)");
    s.ctx.fillStyle = g;
    s.ctx.fillRect(rx, ry, 3 + hash1(seed + 32, i) * 7, 34);
  }
  const tex = texture(s.canvas, t);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  return tex;
}

/** Wet paving: the albedo/normal/rough set for a street that reflects. */
export interface WetStreet {
  albedo: THREE.CanvasTexture;
  normal: THREE.CanvasTexture;
  rough: THREE.CanvasTexture;
}

/**
 * Sett paving under standing water. The reference has no floor at all
 * (it is a mid-air canyon view), so this answers Bebop's own framing: the
 * night vistas give a third of the plate to the ground, and a ground that
 * does not reflect the signage is the loudest thing in the frame saying
 * this is not a wet night city.
 *
 * Wetness is a low-frequency field. Where it is high the setts darken, the
 * roughness collapses toward a mirror and the normal flattens (water fills
 * the joints) — which is exactly how a real wet street behaves and why the
 * puddles read as puddles without a single puddle-shaped decal.
 */
export function wetStreet(t: Tracker, seed = 12): WetStreet {
  const n = 512;
  const alb = sheet(n, n);
  const nrm = sheet(n, n);
  const rgh = sheet(n, n);
  const wet = makeFbm(seed, [2, 5]);
  const grit = makeFbm(seed + 7, [64, 160]);
  const crack = makeFbm(seed + 13, [9, 21]);
  const height = new Float32Array(n * n);
  const wetness = new Float32Array(n * n);
  const aImg = alb.ctx.createImageData(n, n);
  const rImg = rgh.ctx.createImageData(n, n);
  const ad = aImg.data;
  const rd = rImg.data;
  const cols = 26;
  const rows = 34;
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      const i = y * n + x;
      // Setts: staggered courses, each with its own value and a slight dome.
      const row = Math.floor((y / n) * rows);
      const off = (row % 2) * 0.5;
      const cu = (x / n) * cols + off;
      const col = Math.floor(cu);
      const fu = cu - col;
      const fv = (y / n) * rows - row;
      const idx = row * 97 + col;
      const jitter = hash1(seed + 31, idx);
      // Joint: distance to the nearest cell edge, softened.
      const joint = Math.min(Math.min(fu, 1 - fu) * cols, Math.min(fv, 1 - fv) * rows) / 3.2;
      const jointK = Math.min(1, Math.max(0, joint));
      const dome = Math.sin(Math.min(1, jointK) * Math.PI * 0.5);
      const g = grit(x / n, y / n);
      const wv = Math.max(0, wet(x / n, y / n) * 1.25 - 0.32);
      wetness[i] = wv;
      height[i] = dome * 0.7 + g * 0.14 + jitter * 0.1 + crack(x / n, y / n) * 0.06;
      // Base stone: cool grey, per-sett value spread, gritty.
      let base = 74 + jitter * 34 + (g - 0.5) * 22;
      base *= 0.55 + 0.45 * jointK;
      // Water darkens and saturates what it covers, and it pools in joints.
      const pooled = Math.min(1, wv * 1.4 + (1 - jointK) * wv * 2);
      const r = base * (1 - pooled * 0.42);
      const gg = base * 1.06 * (1 - pooled * 0.38);
      const b = base * 1.18 * (1 - pooled * 0.3);
      ad[i * 4] = clamp255(r);
      ad[i * 4 + 1] = clamp255(gg);
      ad[i * 4 + 2] = clamp255(b);
      ad[i * 4 + 3] = 255;
      // Roughness: dry stone is 0.92, standing water is 0.06. The whole wet
      // look is this one channel.
      // Floored at 0.11, not 0.05. A near-mirror puddle turns a gaslamp's
      // point light into a two-pixel specular that renders at 255 — and §e's
      // one absolute is that nothing in a night frame hard-clips. 0.11
      // spreads the same energy over ~3× the area at a fifth of the peak,
      // which is both legal and what a rippled puddle actually does.
      const rough = 0.92 - pooled * 0.78 - (1 - jointK) * 0.06;
      rd[i * 4] = clamp255(Math.max(0.11, rough) * 255);
      rd[i * 4 + 1] = clamp255(Math.max(0.11, rough) * 255);
      rd[i * 4 + 2] = clamp255(Math.max(0.11, rough) * 255);
      rd[i * 4 + 3] = 255;
    }
  }
  // Water flattens the relief it covers: the normal is the sett height
  // faded out by wetness, which is why a puddle looks like glass.
  const nImg = nrm.ctx.createImageData(n, n);
  const nd = nImg.data;
  for (let y = 0; y < n; y++) {
    const ym = ((y - 1 + n) % n) * n;
    const yp = ((y + 1) % n) * n;
    const yc = y * n;
    for (let x = 0; x < n; x++) {
      const xm = (x - 1 + n) % n;
      const xp = (x + 1) % n;
      const flat = 1 - Math.min(1, (wetness[yc + x] ?? 0) * 1.6);
      const dx = ((height[yc + xp] ?? 0) - (height[yc + xm] ?? 0)) * 5 * flat;
      const dy = ((height[yp + x] ?? 0) - (height[ym + x] ?? 0)) * 5 * flat;
      const inv = 1 / Math.sqrt(dx * dx + dy * dy + 1);
      const o = (yc + x) * 4;
      nd[o] = clamp255((-dx * inv * 0.5 + 0.5) * 255);
      nd[o + 1] = clamp255((dy * inv * 0.5 + 0.5) * 255);
      nd[o + 2] = clamp255(inv * 255);
      nd[o + 3] = 255;
    }
  }
  alb.ctx.putImageData(aImg, 0, 0);
  nrm.ctx.putImageData(nImg, 0, 0);
  rgh.ctx.putImageData(rImg, 0, 0);
  const wrap = (tex: THREE.CanvasTexture, srgb: boolean): THREE.CanvasTexture => {
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    tex.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
    tex.anisotropy = 8;
    return tex;
  };
  return {
    albedo: wrap(texture(alb.canvas, t), true),
    normal: wrap(texture(nrm.canvas, t), false),
    rough: wrap(texture(rgh.canvas, t), false),
  };
}
